import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { LegalModal } from "../components/legal/LegalModal";
import { Popup } from "../components/Popup";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useWatchedOrigination } from "../hooks/useWatchedOrigination";
import { useLaunchCashDepositPayment } from "../hooks/useLaunchCashDepositPayment";
import { isUnfundedCashDeposit } from "../api/cash-deposit";
import { cancelOperation, fetchOperationDocument } from "../services/api";
import { formatHeroAmount } from "../utils";
import { CheckIcon, CopyIcon, LimitInfoIcon, StatusCircleIcon, StatusFailedIcon, StatusPendingIcon } from "../assets/icons";
import {
  STATUS_TONE_COLOR,
  originationStatusKey,
  originationStatusTone,
  isOriginationComplete,
  type OriginationStatusTone,
} from "../utils/bindingStatus";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import {
  shouldOfferNativeShare,
  shareOrDownloadBlob,
} from "../utils/shareReceipt";
import { buildReceiptPdf } from "../utils/receiptPdf";
import {
  buildReceiptRows,
  referenceLabel,
  splitVisibleReferences,
  toReceiptOperation,
} from "../utils/receiptRows";
import { getEvents, safeCapture } from "../analytics";
import "../styles/legal.style.css";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import {
  buildShareText,
  pickShareFirstName,
  shareOrCopyMessage,
} from "../utils/shareMessage";
import { useRequestStore } from "../store/useRequestStore";
import { Navbar } from "../components/Navbar";


const RECEIPT_KINDS = ["SENDER_RECEIPT", "PAYOUT_RECEIPT"] as const;

const statusIcon = (tone: OriginationStatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

/**
 * Receipt for a deferred payout send (sender perspective).
 */
export const ReceiptSummaryPage = memo(() => {
  const { t, i18n } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { mintResult } = useRequestStore();
  const { redirectTo } = useRouteWithToken();
  const { originationId } = useParams<{ originationId: string }>();
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const [sharing, setSharing] = useState(false);
  const [copiedType, setCopiedType] = useState<string | null>(null);
  const sharingRef = useRef(false);
  const offerNativeShare = useMemo(
    () => shouldOfferNativeShare("application/pdf"),
    []
  );
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "en").split("-")[0];

  const colorConfig = theme.whiteView;
  const { bodyCopy, dropzoneColors, title, footerColor } =
    colorConfig;
  const { detail, status, refresh } = useWatchedOrigination(
    session.data?.linkId,
    originationId
  );
  const loading = status === "loading";
  const fetchFailed = status === "missing";
  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<"success" | "error" | null>(
    null
  );

  const linkId = session.data?.linkId;
  const canCancel = detail?.operation?.cancellation?.allowed === true;
  const cancelMode = detail?.operation?.cancellation?.mode;
  const operationId = detail?.operationId ?? detail?.operation?.operationId;

  useEffect(() => {
    const cancellation = detail?.operation?.cancellation;
    if (!cancellation?.allowed || !cancellation.expiresAt) {
      return;
    }
    const ms = new Date(cancellation.expiresAt).getTime() - Date.now();
    if (ms <= 0) return;
    const id = window.setTimeout(() => {
      void refresh();
    }, ms);
    return () => window.clearTimeout(id);
  }, [
    detail?.operation?.cancellation?.allowed,
    detail?.operation?.cancellation?.expiresAt,
    refresh,
  ]);

  const handleCancelConfirm = useCallback(async () => {
    if (!linkId || !operationId) return;
    setCancelling(true);
    setCancelResult(null);
    try {
      const result = await cancelOperation(linkId, operationId);
      if (result.mode === "request") {
        redirectTo(APP_ROUTES.CANCELLATION_IN_PROGRESS_PAGE, { replace: true });
        return;
      }
      await refresh();
      setCancelResult("success");
    } catch {
      if (originationId) {
        redirectTo(
          `${APP_ROUTES.CANCELLATION_FAILED_PAGE}/${originationId}`,
          { replace: true },
        );
      }
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  }, [linkId, operationId, originationId, refresh, redirectTo]);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("done"), {
      origination_id: originationId,
    });
  }, [originationId]);

  const handleCopyReference = useCallback(
    async (type: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopiedType(type);
        window.setTimeout(() => {
          setCopiedType((current) => (current === type ? null : current));
        }, 2000);
        safeCapture(getEvents().PAYOUT_CODE_COPY, {
          operation_id: detail?.operationId,
          reference_type: type,
        });
      } catch {
        // Clipboard may be unavailable (insecure context / denied permission).
      }
    },
    [detail?.operationId]
  );

  const handleShare = async () => {
    if (sharingRef.current || !detail) return;
    sharingRef.current = true;
    setSharing(true);
    const fileId = (detail.operationId ?? originationId) ?? "recibo";
    const shareTitle = t("receipt.shareTitle");
    const linkId = session.data?.linkId;
    try {
      const receiptKind = RECEIPT_KINDS.find((kind) =>
        detail.operation?.documents?.some(
          (doc) => doc.kind === kind && doc.availability === "AVAILABLE"
        )
      );

      let result: Awaited<ReturnType<typeof shareOrDownloadBlob>> | "skipped" =
        "skipped";
      if (receiptKind && linkId && detail.operationId) {
        try {
          const blob = await fetchOperationDocument(
            linkId,
            detail.operationId,
            receiptKind
          );
          result = await shareOrDownloadBlob({
            blob,
            fileName: t("receipt.shareFileName", { id: fileId }),
            title: shareTitle,
            mediaType: "application/pdf",
          });
        } catch {
          result = "skipped";
        }
      }

      if (result === "skipped") {
        const receiptOp = toReceiptOperation(detail);
        const { prominent } = splitVisibleReferences(receiptOp.references);
        const statusLabel = t(
          originationStatusKey(detail.bindingState, detail.operation?.status)
        );
        const blob = await buildReceiptPdf({
          operation: receiptOp,
          rows: buildReceiptRows(detail, t, { lang, statusLabel }),
          prominentRefs: prominent,
          partnerName: session.data?.partner?.name,
          strings: {
            documentTitle: t("receipt.documentTitle"),
            youTransferred: detail.operation
              ? t("receipt.youSent")
              : t("receipt.youSentPending"),
            transactionId: detail.operation
              ? t("receipt.transactionId")
              : t("receipt.originationId"),
            footerNote: t("receipt.footerNote"),
          },
          t,
          lang,
        });
        result = await shareOrDownloadBlob({
          blob,
          fileName: t("receipt.shareFileName", { id: fileId }),
          title: shareTitle,
          mediaType: "application/pdf",
        });
      }

      if (result === "shared" || result === "downloaded") {
        safeCapture(getEvents().SHARE_CLICK, {
          source: "receipt_card",
          method: result === "shared" ? "native" : "download",
          asset: "pdf",
        });
      }
    } finally {
      sharingRef.current = false;
      setSharing(false);
    }
  };

  const {
    launch: launchCashDeposit,
    launching: launchingCashDeposit,
    error: cashLaunchError,
  } = useLaunchCashDepositPayment(session.data?.linkId);

  const canPayAtStore = useMemo(() => {
    if (!detail?.operation) return false;
    return isUnfundedCashDeposit(
      detail.operation.fundingMethod ?? detail.sendCommitment.fundingMethod,
      detail.operation.fundingStatus
    );
  }, [detail]);

  const canShareClaim = Boolean(
    detail &&
      !detail.operation &&
      !canPayAtStore &&
      (detail.bindingState === "payment_bound" ||
        detail.bindingState === "awaiting_claim") &&
      detail.payoutClaimUrl
  );

  const canDownloadReceipt = Boolean(
    detail &&
      !canPayAtStore &&
      isOriginationComplete(detail.bindingState, detail.operation?.status)
  );

  const handleShareClaim = async () => {
    if (!detail?.payoutClaimUrl) return;
    safeCapture(getEvents().SHARE_CLICK, { method: "share" });
    const mintedSenderFirstName =
      mintResult?.originationId === detail.originationId
        ? mintResult.senderFirstName
        : undefined;
    const message = buildShareText(t, {
      firstName: pickShareFirstName(
        mintedSenderFirstName,
        session.data?.userData?.name?.firstName
      ),
      amount: detail.sendCommitment.sendAmount,
      currency: detail.sendCommitment.sendCurrency,
      url: detail.payoutClaimUrl,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <section className="receipt-summary-loading">
          <span
            className="loader-spinner"
            style={
              {
                "--spinner-start": bodyCopy,
                "--spinner-end": bodyCopy,
              } as CSSProperties
            }
          />
        </section>
      );
    }

    if (fetchFailed || !detail) {
      return (
        <section className="receipt-summary-loading">
          <BodyCopy textColor={bodyCopy}>{t("receipt.notFound")}</BodyCopy>
        </section>
      );
    }

    const operation = detail.operation ?? null;
    const sc = detail.sendCommitment;
    const source = sc.sendCurrency;
    const hasOperation = operation != null;

    const heroAmount = hasOperation ? operation.sendAmount : sc.sendAmount;
    const { prefix, suffix, intPart: heroInt, decPart: heroDec } = formatHeroAmount(
      heroAmount,
      source
    );

    const statusTone: OriginationStatusTone = canPayAtStore
      ? "progress"
      : originationStatusTone(detail.bindingState, operation?.status);
    const statusValue = canPayAtStore
      ? t("receipt.statusPendingCash")
      : t(originationStatusKey(detail.bindingState, operation?.status));
    const statusColor = STATUS_TONE_COLOR[statusTone];
    const { prominent: prominentRefs } = splitVisibleReferences(
      operation?.references
    );
    const rows = buildReceiptRows(detail, t, {
      lang,
      statusLabel: statusValue,
    });

    const idLabel = hasOperation
      ? t("receipt.transactionId")
      : t("receipt.originationId");
    const idValue = hasOperation
      ? operation.operationId
      : detail.originationId;

    return (
      <section className="receipt-summary-container">
        <section className="receipt-summary-scroll">
          <ScrollFadeIndicator color={bodyCopy} />
        <section className="summary-section">
          <section className="receipt-card-capture">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>
              {hasOperation ? t("receipt.youSent") : t("receipt.youSentPending")}
            </BodyCopy>
            <GradientText as="h1">
              {prefix}{heroInt}.<label>{heroDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={idLabel}
            >
              {idValue.slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          {prominentRefs.map((ref) => (
            <section
              key={`${ref.type}:${ref.value}`}
              className="receipt-reference-hero"
              style={{ borderColor: "#eeeeee" }}
            >
              <BodyCopy textColor={bodyCopy}>
                {referenceLabel(ref.type, ref.label, t, lang)}
              </BodyCopy>
              <div className="receipt-reference-value-wrap">
                <p className="receipt-reference-value" style={{ color: TITLE_COLOR }}>
                  {ref.value}
                </p>
                <button
                  type="button"
                  className="receipt-reference-copy"
                  style={{ color: bodyCopy }}
                  aria-label={
                    copiedType === ref.type
                      ? t("receipt.copied")
                      : t("receipt.copy")
                  }
                  onClick={() => void handleCopyReference(ref.type, ref.value)}
                >
                  {copiedType === ref.type ? (
                    <CheckIcon size={16} />
                  ) : (
                    <CopyIcon size={16} />
                  )}
                </button>
              </div>
            </section>
          ))}
          <section className="summary-info">
            {rows.map((row) => (
              <SummaryInfoItem
                key={row.id}
                status={row.label}
                value={row.value}
                isBold={row.emphasis}
                statusColor={row.id === "status" ? statusColor : undefined}
                icon={
                  row.id === "status"
                    ? statusIcon(statusTone, statusColor)
                    : undefined
                }
              />
            ))}
          </section>
          </section>
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
          <BodyCopy className="footer-text">
            <GradientText
              as="button"
              type="button"
              className="receipt-cancel-policy"
              textColor={title}
              onClick={() => setShowTransactionPolicyModal(true)}
            >
              {t("receipt.cancelationPolicy")}
            </GradientText>
          </BodyCopy>
        </section>
        </section>
      </section>
    );
  };

  const { openSupport, supportSheet } = useSupportSheet();

  return (
    <>
      <>
        <AuraLayout
          colorConfig={colorConfig}
          className="need-help-header receipt-summary-section"
        >
          <Layout.Header>
            <Navbar
              title={t("common.needHelp")}
              align="right"
              onBackClick={() => redirectTo(APP_ROUTES.HOME_PAGE)}
              onIconClick={() => openSupport()}
              icon={<SupportIcon size={35} colors={dropzoneColors} />}
            />
          </Layout.Header>
          <Layout.Content className="receipt-summary-content">{renderContent()}</Layout.Content>
          <Layout.Footer>
            <section className="footer-section transactions-footer">
              {canPayAtStore && detail?.operationId && (
                <>
                  <Button
                    size="large"
                    background={colorConfig.backgroundBtn}
                    color={colorConfig.textColorBtn}
                    disabled={launchingCashDeposit}
                    onClick={() => void launchCashDeposit(detail.operationId!)}
                  >
                    {launchingCashDeposit
                      ? t("receipt.openingCashDeposit")
                      : t("receipt.payAtStore")}
                  </Button>
                  {cashLaunchError && (
                    <BodyCopy className="footer-text" textColor={footerColor}>
                      {cashLaunchError === "launchFailed"
                        ? t("receipt.cashDepositLaunchFailed")
                        : cashLaunchError}
                    </BodyCopy>
                  )}
                  <BodyCopy className="footer-text" textColor={footerColor}>
                    {t("receipt.footerNoteCash")}
                  </BodyCopy>
                </>
              )}
              {canCancel && !cancelResult && (
                <BodyCopy className="footer-text">
                  <button
                    type="button"
                    className="receipt-cancel-transfer"
                    style={{ color: TITLE_COLOR }}
                    onClick={() => setShowConfirm(true)}
                  >
                    {t("receipt.cancelTransfer")}
                  </button>
                </BodyCopy>
              )}
              {cancelResult === "success" && (
                <BodyCopy textColor={bodyCopy} className="cancel-feedback">
                  {t("receipt.cancelSuccess")}
                </BodyCopy>
              )}
              {canShareClaim && (
                <section className="receipt-share-cta">
                  <Button
                    size="large"
                    background={colorConfig.backgroundBtn}
                    color={colorConfig.textColorBtn}
                    onClick={() => void handleShareClaim()}
                  >
                    {t("share.share")}
                  </Button>
                </section>
              )}
              {canDownloadReceipt && (
              <section className="receipt-share-cta">
                <Button
                  size="large"
                  background={colorConfig.backgroundBtn}
                  color={colorConfig.textColorBtn}
                  onClick={() => void handleShare()}
                  disabled={sharing}
                >
                  {offerNativeShare
                    ? t("receipt.shareReceipt")
                    : t("receipt.downloadReceipt")}
                </Button>
              </section>
              )}
            </section>
          </Layout.Footer>
        </AuraLayout>
        <LegalModal
          doc="transactionPolicy"
          visible={showTransactionPolicyModal}
          onClose={() => setShowTransactionPolicyModal(false)}
        />
        <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
          <section className="cancel-confirm-slot">
            <LimitInfoIcon
              className="cancel-confirm-icon"
              size={60}
              colors={[TITLE_COLOR, TITLE_COLOR]}
            />
            <Title title={t("receipt.cancelConfirmTitle")} align="center" color={TITLE_COLOR} />
            <BodyCopy textColor={bodyCopy}>
              {t(
                cancelMode === "request"
                  ? "receipt.cancelConfirmMessageRequest"
                  : "receipt.cancelConfirmMessageVoid"
              )}
            </BodyCopy>
            <div className="cancel-confirm-actions">
              <Button
                size="large"
                variant="outline"
                background={colorConfig.backgroundBtn}
                color={TITLE_COLOR}
                style={{ backgroundColor: colorConfig.background }}
                onClick={() => void handleCancelConfirm()}
                disabled={cancelling}
              >
                {cancelling ? "..." : t("receipt.cancelConfirmYes")}
              </Button>
              <Button
                size="large"
                background={colorConfig.backgroundBtn}
                color={colorConfig.textColorBtn}
                onClick={() => setShowConfirm(false)}
                disabled={cancelling}
              >
                {t("receipt.cancelConfirmNo")}
              </Button>
            </div>
          </section>
        </Popup>
      </>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
